import React, { createContext, useState, useContext, useCallback } from 'react';
import BlurLoader from '../components/loaders/BlurLoader';
import SkeletonManager from '../components/loaders/SkeletonManager';

const LoadingContext = createContext();

export function UnifiedLoadingProvider({ children }) {
  const [globalLoading, setGlobalLoading] = useState({
    active: false,
    mode: 'blur', // 'blur' | 'skeleton'
    text: 'Chargement des données...',
    showLogo: true,
    logo: '/icon.png'
  });

  const [skeletonActive, setSkeletonActive] = useState(false);

  // Unified setLoading function
  const setLoading = useCallback((options) => {
    // Handle boolean for backward compatibility - defaults to blur
    if (typeof options === 'boolean') {
      setGlobalLoading(prev => ({
        ...prev,
        active: options,
        mode: 'blur'
      }));
      setSkeletonActive(false);
      return;
    }

    // Handle object options
    if (typeof options === 'object' && options !== null) {
      const { mode = 'blur', text = 'Chargement des données...', isLoading = true } = options;

      if (mode === 'skeleton') {
        // Skeleton mode - just show/hide, no config needed
        setSkeletonActive(isLoading);
        setGlobalLoading(prev => ({ ...prev, active: false }));
      } else {
        // Blur mode (default)
        setGlobalLoading({
          active: isLoading,
          mode: 'blur',
          text,
          showLogo: options.showLogo !== undefined ? options.showLogo : true,
          logo: options.logo || '/icon.png'
        });
        setSkeletonActive(false);
      }
    }
  }, []);

  // Get loading state
  const getLoadingState = useCallback(() => {
    if (globalLoading.active) return globalLoading;
    if (skeletonActive) return { active: true, mode: 'skeleton' };
    return { active: false };
  }, [globalLoading, skeletonActive]);

  // Check if any loading is active
  const isLoading = globalLoading.active || skeletonActive;

  return (
    <LoadingContext.Provider value={{ 
      setLoading,
      setIsLoading: setLoading, // Backward compatibility alias
      isLoading,
      getLoadingState,
      globalLoading,
      skeletonActive 
    }}>
      {children}
      <BlurLoader {...globalLoading} />
      <SkeletonManager active={skeletonActive} />
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within UnifiedLoadingProvider');
  }
  return context;
}

// Backward compatibility export
export { UnifiedLoadingProvider as LoadingProvider };